import { useEffect, useState } from "react";
import { History, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api } from "@/lib/api";
import { VERSION_HISTORY } from "@/constants/testIds";

export function VersionHistoryDialog({ docType, templateName, employees, defaultEmployeeId, needsEmployee }) {
  const [open, setOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState(defaultEmployeeId || "");
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);

  const load = (empId) => {
    if (needsEmployee && !empId) {
      setVersions([]);
      return;
    }
    setLoading(true);
    api
      .get(`/document-templates/${docType}/versions`, { params: empId ? { employee_id: empId } : {} })
      .then(({ data }) => setVersions(data.versions))
      .finally(() => setLoading(false));
  };

  const handleOpen = (v) => {
    setOpen(v);
    if (v) {
      const empId = defaultEmployeeId || "";
      setEmployeeId(empId);
      load(empId);
    }
  };

  const handleEmployeeChange = (val) => {
    setEmployeeId(val);
    load(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={`${VERSION_HISTORY.dialogTriggerPrefix}-${docType}`}
          size="sm" variant="ghost"
          className="h-7 text-xs text-charcoal/50 hover:text-petroleum px-2"
        >
          <History className="w-3.5 h-3.5 mr-1" /> Historial
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={VERSION_HISTORY.dialog} className="bg-bone max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Historial de versiones: {templateName}</DialogTitle>
          <DialogDescription>Versiones generadas previamente de este documento, incluida la vigente.</DialogDescription>
        </DialogHeader>

        {needsEmployee && (
          <Select value={employeeId} onValueChange={handleEmployeeChange}>
            <SelectTrigger data-testid={VERSION_HISTORY.employeeSelect} className="bg-white"><SelectValue placeholder="Selecciona un empleado" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {loading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-petroleum animate-spin" /></div>}

        {!loading && needsEmployee && !employeeId && (
          <p className="text-sm text-charcoal/50 text-center py-4">Selecciona un empleado para ver sus versiones.</p>
        )}

        {!loading && versions.length === 0 && (needsEmployee ? employeeId : true) && (
          <p className="text-sm text-charcoal/50 text-center py-4">Aún no se ha generado ninguna versión.</p>
        )}

        <div className="space-y-2">
          {versions.map((v) => (
            <div key={v.id} data-testid={VERSION_HISTORY.versionRow} className="bg-card border border-petroleum/10 rounded-xl p-3 flex justify-between items-center gap-3">
              <div>
                <p className="text-sm text-charcoal">{new Date(v.created_at).toLocaleString("es-MX")}</p>
                {v.generated_by && (
                  <p data-testid={VERSION_HISTORY.generatedByLabel} className="text-xs text-charcoal/50">Generado por {v.generated_by}</p>
                )}
                {v.kind === "policy" && (
                  <p className="text-xs text-charcoal/50">{v.assigned_count} asignada(s) · {v.acknowledged_count} confirmada(s)</p>
                )}
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${v.is_vigente ? "bg-emerald-50 text-emerald-700" : "bg-charcoal/5 text-charcoal/40"}`}>
                {v.status_label}
              </span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
